import { useEffect, useMemo, useState } from "react";
import { useClientAthlete } from "../../../context/ClientAthleteContext";
import clinicAPI from "../../../services/clinicApi";
import {
  currentRsvpSummary,
  findAttendanceOptions,
  isPollOpen,
  buildPollRespondPayload,
  notifyPollAnswered,
} from "../../../utils/clientSessionUtils";
import { useDemoPortalOptional } from "../../../demoPortal/DemoPortalContext";
import s from "./clientApp.module.css";
import { useToast } from "../../ui";

function sessionIdOf(session) {
  return session?.session_id || session?.id;
}

function isTrainingSession(session) {
  if (!session || session.session_kind === "event") return false;
  return (
    session.source === "training_session" ||
    Boolean(session.session_id || session.id)
  );
}

function responseLabel(summary) {
  if (!summary) return null;
  if (summary.forecast === "coming") {
    return { title: "Буду", tone: "yes" };
  }
  if (summary.forecast === "not_coming") {
    return {
      title: "Не буду",
      tone: "no",
      reason: summary.reason,
    };
  }
  return null;
}

function rsvpCapacityHint(session, mineComing = false) {
  const capacity = Number(session?.rsvp_capacity);
  if (!Number.isFinite(capacity) || capacity < 1) return null;
  const spotsLeft = Number(session?.rsvp_spots_left);
  const coming = Number(session?.rsvp_coming_count);
  const left = Number.isFinite(spotsLeft)
    ? spotsLeft
    : Number.isFinite(coming)
    ? Math.max(0, capacity - coming)
    : null;
  const full =
    session?.rsvp_full === true ||
    (left === 0 && !mineComing);
  return {
    capacity,
    spotsLeft: left,
    full,
    label:
      left == null
        ? `Мест на занятие: ${capacity}`
        : full
          ? `Мест нет (лимит ${capacity})`
          : mineComing && left === 0
            ? `Вы записаны · мест больше нет (лимит ${capacity})`
            : `Свободно мест: ${left} из ${capacity}`,
  };
}

function isCompetitionSession(session) {
  return session?.session_kind === "event" && session?.event_kind === "competition";
}

export default function AttendancePollButtons({
  session,
  pollRow,
  onDone,
  onPhaseChange,
  compact = false,
  variant = "default",
  question = "Придёте на тренировку?",
  allowDirect = true,
}) {
  const toast = useToast();
  const demo = useDemoPortalOptional();
  const { athleteQueryParam } = useClientAthlete();
  const [phase, setPhase] = useState("choose");
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const [editing, setEditing] = useState(false);
  const isCompetition = isCompetitionSession(session);

  const { yes, no, all } = findAttendanceOptions(pollRow);
  const hasPoll = pollRow && isPollOpen(pollRow);
  const sid = pollRow?.session_id || pollRow?.session?.id || sessionIdOf(session);
  const canPollApi =
    Boolean(pollRow?.dispatch_id) || Boolean(pollRow?.poll_id);
  const usePollFlow =
    hasPoll && canPollApi && (yes || all.length > 0);
  const canDirect = allowDirect && isTrainingSession(session) && sessionIdOf(session);
  const current = useMemo(
    () => currentRsvpSummary(session, pollRow),
    [session, pollRow],
  );
  const answered = Boolean(current);
  const showForm = !answered || editing || phase === "reason";

  useEffect(() => {
    const uiPhase =
      phase === "reason"
        ? "reason"
        : !showForm && answered
          ? "answered"
          : "choose";
    onPhaseChange?.(uiPhase);
  }, [phase, showForm, answered, onPhaseChange]);

  const wrapClass =
    variant === "sheet" ? s.pollSheet : compact ? s.pollCompact : s.pollBlock;

  const submitPoll = async (optionId, reasonText) => {
    if (demo?.isDemo) {
      const opt = (pollRow?.options || pollRow?.poll?.options || []).find(
        (o) => o.id === optionId,
      );
      const forecast = opt?.forecast || "coming";
      demo.setRsvp(
        pollRow.session_id || pollRow.session?.id || sessionIdOf(session),
        forecast,
        reasonText || null,
      );
      return;
    }
    const payload = {
      option_id: optionId,
      reason: reasonText || undefined,
    };
    if (pollRow.poll_id) payload.poll_id = pollRow.poll_id;
    const sid = pollRow.session_id || pollRow.session?.id || sessionIdOf(session);
    if (sid) payload.session_id = sid;
    const { routeId, body } = buildPollRespondPayload(pollRow, payload);
    await clinicAPI.respondTrainingPoll(routeId, body, athleteQueryParam);
  };

  const submitDirect = async (coming, reasonText) => {
    if (demo?.isDemo) {
      demo.setRsvp(
        sessionIdOf(session),
        coming ? "coming" : "not_coming",
        reasonText || null,
      );
      return;
    }
    await clinicAPI.postSessionRsvp(
      sessionIdOf(session),
      {
        coming,
        reason: reasonText || undefined,
      },
      athleteQueryParam,
    );
  };

  const finish = () => {
    toast.success(demo?.isDemo ? "Сохранено только в демо" : "Ответ сохранён");
    setPhase("choose");
    setReason("");
    setEditing(false);
    notifyPollAnswered();
    onDone?.();
  };

  const handleYes = async () => {
    if (busy) return;
    setBusy("yes");
    try {
      if (usePollFlow && yes) {
        await submitPoll(yes.id);
      } else if (canDirect) {
        await submitDirect(true);
      } else {
        toast.error("Не удалось отправить ответ. Откройте уведомления или обновите страницу.");
        return;
      }
      finish();
    } catch (e) {
      toast.error(e.response?.data?.error || "Не удалось отправить");
    } finally {
      setBusy(null);
    }
  };

  const handleNoClick = () => {
    setPhase("reason");
    setReason("");
  };

  const handleNoConfirm = async () => {
    const trimmed = reason.trim();
    if (!trimmed) {
      toast.error("Укажите причину");
      return;
    }
    if (busy) return;
    setBusy("no");
    try {
      if (usePollFlow && no) {
        await submitPoll(no.id, trimmed);
      } else if (canDirect) {
        await submitDirect(false, trimmed);
      } else {
        toast.error("Не удалось отправить ответ. Откройте уведомления или обновите страницу.");
        return;
      }
      finish();
    } catch (e) {
      toast.error(e.response?.data?.error || "Не удалось отправить");
    } finally {
      setBusy(null);
    }
  };

  if (isCompetition) return null;

  const sessionStart = session?.start_datetime
    ? new Date(session.start_datetime).getTime()
    : NaN;
  const sessionStarted =
    Number.isFinite(sessionStart) && sessionStart <= Date.now();
  if (sessionStarted && !answered) return null;
  if (sessionStarted && answered) {
    const label = responseLabel(current);
    if (!label) return null;
    return (
      <div className={`${wrapClass} ${variant === "sheet" ? s.pollSheetAnswered : ""}`}>
        <div className={s.rsvpAnswerRow}>
          <span
            className={`${s.rsvpAnswerBadge} ${
              label.tone === "yes" ? s.rsvpAnswerBadgeYes : s.rsvpAnswerBadgeNo
            }`}
          >
            {label.title}
          </span>
        </div>
        {label.reason ? <p className={s.rsvpAnswerReason}>{label.reason}</p> : null}
        <p className={s.rsvpCapacityHint}>Занятие уже началось — ответ изменить нельзя</p>
      </div>
    );
  }

  if (!usePollFlow && !canDirect) return null;

  if (!showForm && answered) {
    const label = responseLabel(current);
    if (!label) return null;
    const canChange = (usePollFlow && isPollOpen(pollRow)) || canDirect;
    return (
      <div className={`${wrapClass} ${variant === "sheet" ? s.pollSheetAnswered : ""}`}>
        <div className={s.rsvpAnswerRow}>
          <span
            className={`${s.rsvpAnswerBadge} ${
              label.tone === "yes" ? s.rsvpAnswerBadgeYes : s.rsvpAnswerBadgeNo
            }`}
          >
            {label.title}
          </span>
          {canChange ? (
            <button
              type="button"
              className={s.rsvpChangeBtn}
              onClick={() => setEditing(true)}
            >
              Изменить
            </button>
          ) : null}
        </div>
        {label.reason ? <p className={s.rsvpAnswerReason}>{label.reason}</p> : null}
      </div>
    );
  }

  if (phase === "reason") {
    return (
      <div className={`${wrapClass} ${variant === "sheet" ? s.pollSheetReason : ""}`}>
        {variant === "sheet" ? (
          <>
            <p className={s.pollSheetReasonTitle}>Причина отсутствия</p>
            <p className={s.pollSheetReasonHint}>
              Кратко опишите, почему не сможете прийти
            </p>
          </>
        ) : (
          <p className={s.pollQuestion}>Почему не сможете прийти?</p>
        )}
        <textarea
          className={s.rsvpReasonInput}
          rows={3}
          placeholder="Например: болею, командировка, экзамены…"
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          maxLength={500}
        />
        <div className={s.rsvpReasonActions}>
          <button
            type="button"
            className={s.rsvpBackBtn}
            disabled={!!busy}
            onClick={() => {
              setPhase("choose");
              setReason("");
              if (answered) setEditing(false);
            }}
          >
            Назад
          </button>
          <button
            type="button"
            className={variant === "sheet" ? s.pollBtnSubmit : s.pollBtnYes}
            disabled={!!busy}
            onClick={handleNoConfirm}
          >
            {busy === "no" ? "…" : "Отправить"}
          </button>
        </div>
      </div>
    );
  }

  const yesLabel = "Буду";
  const noLabel = "Не буду";
  const mineComing = current?.forecast === "coming";
  const capacityHint = rsvpCapacityHint(session, mineComing);
  const yesDisabled = !!busy || Boolean(capacityHint?.full);
  const capacityNote = capacityHint ? (
    <p className={s.rsvpCapacityHint}>{capacityHint.label}</p>
  ) : null;

  if (usePollFlow && yes && no) {
    return (
      <div className={wrapClass}>
        {variant === "sheet" ? (
          <p className={s.pollSheetQuestion}>{question}</p>
        ) : (
          <p className={s.pollQuestion}>{question}</p>
        )}
        {capacityNote}
        <div className={s.pollBtnRow}>
          <button
            type="button"
            className={s.pollBtnYes}
            disabled={yesDisabled}
            onClick={handleYes}
          >
            {busy === "yes" ? "…" : yesLabel}
          </button>
          <button
            type="button"
            className={s.pollBtnNo}
            disabled={!!busy}
            onClick={handleNoClick}
          >
            {noLabel}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className={wrapClass}>
      {variant === "sheet" ? (
        <p className={s.pollSheetQuestion}>{question}</p>
      ) : (
        <p className={s.pollQuestion}>{question}</p>
      )}
      {capacityNote}
      {usePollFlow && all.length > 0 ? (
        <div className={s.pollBtnRow}>
          {all.map((opt) => {
            const isNo =
              opt.forecast === "not_coming" ||
              /не приду|не смогу|не буду/i.test(opt.text || "");
            return (
              <button
                key={opt.id}
                type="button"
                className={isNo ? s.pollBtnNo : s.pollBtnYes}
                disabled={!!busy || (!isNo && yesDisabled)}
                onClick={() => {
                  if (isNo) {
                    handleNoClick();
                    return;
                  }
                  if (busy || yesDisabled) return;
                  setBusy(opt.id);
                  submitPoll(opt.id)
                    .then(finish)
                    .catch((e) =>
                      toast.error(e.response?.data?.error || "Не удалось отправить"),
                    )
                    .finally(() => setBusy(null));
                }}
              >
                {busy === opt.id ? "…" : opt.text}
              </button>
            );
          })}
        </div>
      ) : (
        <div className={s.pollBtnRow}>
          <button
            type="button"
            className={s.pollBtnYes}
            disabled={yesDisabled}
            onClick={handleYes}
          >
            {busy === "yes" ? "…" : yesLabel}
          </button>
          <button
            type="button"
            className={s.pollBtnNo}
            disabled={!!busy}
            onClick={handleNoClick}
          >
            {noLabel}
          </button>
        </div>
      )}
    </div>
  );
}
